import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateDateRange = (dateRange) => {
    if (dateRange === null || dateRange === undefined) {
        return null;
    }

    if (typeof dateRange !== 'object') {
        throw new Error('Date range must be a valid object');
    }

    const validatedRange = {};

    if (dateRange.startDate) {
        if (!(dateRange.startDate instanceof Date) || isNaN(dateRange.startDate.getTime())) {
            throw new Error('Start date must be a valid Date object');
        }
        validatedRange.startDate = dateRange.startDate;
    }

    if (dateRange.endDate) {
        if (!(dateRange.endDate instanceof Date) || isNaN(dateRange.endDate.getTime())) {
            throw new Error('End date must be a valid Date object');
        }
        validatedRange.endDate = dateRange.endDate;
    }

    if (validatedRange.startDate && validatedRange.endDate) {
        if (validatedRange.endDate < validatedRange.startDate) {
            throw new Error('End date cannot be before start date');
        }
    }

    return validatedRange;
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleTeamInsightsAuthError = (error, operation = 'Team Insights API operation') => {
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to access team insights data');
    }

    if (error.response?.status === 404) {
        throw new Error('Team insights data not found or team does not exist');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid team insights parameters provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status >= 500) {
        throw new Error('Team insights service error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// QUERY PARAMS BUILDER
// ============================================================================

const buildQueryParams = (dateRange) => {
    const params = new URLSearchParams();

    if (dateRange && dateRange.startDate) {
        params.append('startDate', dateRange.startDate.toISOString());
    }

    if (dateRange && dateRange.endDate) {
        params.append('endDate', dateRange.endDate.toISOString());
    }

    return params;
};

// ============================================================================
// TEAM INSIGHTS API FUNCTIONS
// ============================================================================

/**
 * Fetch team insights overview data
 * GET /api/team-insights/:teamId
 * @param {string} teamId - Team ID
 * @param {Object} dateRange - { startDate, endDate } (optional)
 * @returns {Promise<Object>} { success: boolean, data: insightsData }
 */
export const fetchTeamInsightsData = async (teamId, dateRange = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validDateRange = validateDateRange(dateRange);

        const params = buildQueryParams(validDateRange);

        const response = await apiClient.get(`${API_BASE_URL}/api/team-insights/${validTeamId}?${params.toString()}`);

        if (response.status === 200) {
            return {
                success: true,
                data: response.data.data
            };
        } else {
            throw new Error('Failed to fetch team insights data');
        }
    } catch (error) {
        handleTeamInsightsAuthError(error, 'Fetching team insights data');
    }
};

/**
 * Fetch team leads insights
 * GET /api/team-insights/:teamId/leads
 * @param {string} teamId - Team ID
 * @param {Object} dateRange - { startDate, endDate } (optional)
 * @returns {Promise<Object>} { success: boolean, data: leadsData }
 */
export const fetchTeamLeadsInsights = async (teamId, dateRange = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validDateRange = validateDateRange(dateRange);

        const params = buildQueryParams(validDateRange);

        const response = await apiClient.get(`${API_BASE_URL}/api/team-insights/${validTeamId}/leads?${params.toString()}`);

        if (response.status === 200) {
            return {
                success: true,
                data: response.data.data
            };
        } else {
            throw new Error('Failed to fetch team leads insights data');
        }
    } catch (error) {
        handleTeamInsightsAuthError(error, 'Fetching team leads insights');
    }
};

/**
 * Fetch team views insights
 * GET /api/team-insights/:teamId/views
 * @param {string} teamId - Team ID
 * @param {Object} dateRange - { startDate, endDate } (optional)
 * @returns {Promise<Object>} { success: boolean, data: viewsData }
 */
export const fetchTeamViewsInsights = async (teamId, dateRange = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validDateRange = validateDateRange(dateRange);

        const params = buildQueryParams(validDateRange);

        const response = await apiClient.get(`${API_BASE_URL}/api/team-insights/${validTeamId}/views?${params.toString()}`);

        if (response.status === 200) {
            return {
                success: true,
                data: response.data.data
            };
        } else {
            throw new Error('Failed to fetch team views insights data');
        }
    } catch (error) {
        handleTeamInsightsAuthError(error, 'Fetching team views insights');
    }
};

/**
 * Fetch team link taps insights
 * GET /api/team-insights/:teamId/link-taps
 * @param {string} teamId - Team ID
 * @param {Object} dateRange - { startDate, endDate } (optional)
 * @returns {Promise<Object>} { success: boolean, data: linkTapsData }
 */
export const fetchTeamLinkTapsInsights = async (teamId, dateRange = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validDateRange = validateDateRange(dateRange);

        const params = buildQueryParams(validDateRange);

        const response = await apiClient.get(`${API_BASE_URL}/api/team-insights/${validTeamId}/link-taps?${params.toString()}`);

        if (response.status === 200) {
            return {
                success: true,
                data: response.data.data
            };
        } else {
            throw new Error('Failed to fetch team link taps insights data');
        }
    } catch (error) {
        handleTeamInsightsAuthError(error, 'Fetching team link taps insights');
    }
};

/**
 * Fetch team recent activity
 * GET /api/team-insights/:teamId/recent-activity
 * @param {string} teamId - Team ID
 * @param {Object} dateRange - { startDate, endDate } (optional)
 * @returns {Promise<Object>} { success: boolean, data: recentActivityData }
 */
export const fetchTeamRecentActivity = async (teamId, dateRange = null) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validDateRange = validateDateRange(dateRange);

        const params = buildQueryParams(validDateRange);

        const response = await apiClient.get(`${API_BASE_URL}/api/team-insights/${validTeamId}/recent-activity?${params.toString()}`);

        if (response.status === 200) {
            return {
                success: true,
                data: response.data.data
            };
        } else {
            throw new Error('Failed to fetch team recent activity data');
        }
    } catch (error) {
        handleTeamInsightsAuthError(error, 'Fetching team recent activity');
    }
};

